// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { useSupportError } from '@/hooks/use-support-error';
import { QuarantinedSerial } from '@/shipment/api/types';
import { QuarantineResolutionDialogShell } from '@/shipment/components/quarantine-resolution-dialog-shell';
import { useResolveQuarantinedSerial } from '@/shipment/hooks/use-resolve-quarantined-serial';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertTitle, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

// The note dialog covers the resolution that needs only a justification. A
// corrected-serial resolution needs an asset picker and has its own dialog.
type NoteResolutionKind = 'KEPT_HARDFIN_ITEM';

const formSchema = z.object({
  note: z.string().min(1, 'A note is required to resolve a held serial'),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  shipmentKey: string;
  quarantinedSerial: QuarantinedSerial;
  kind: NoteResolutionKind;
  isInbound: boolean;
  onClose: () => void;
}

interface Copy {
  title: string;
  consequenceTitle: string;
  overfillConsequenceTitle: string;
  placeholder: string;
  submitLabel: string;
}

function resolutionCopy(existingItemSku: string, isInbound: boolean): Copy {
  const verb = isInbound ? 'receive' : 'ship';

  return {
    title: 'Keep Hardfin item',
    consequenceTitle: `Keep Hardfin item to ${verb} this unit as ${existingItemSku}`,
    overfillConsequenceTitle: `Keep Hardfin item to ${verb} this unit as an extra ${existingItemSku}`,
    placeholder: "Why is the third party's report wrong?",
    submitLabel: 'Keep Hardfin item',
  };
}

/**
 * Resolution dialog for the kind that needs only a note: keep our item, when
 * the reported item is a data error and the unit is ours as recorded.
 */
export function QuarantineResolveNoteDialog(props: Props) {
  const { showSupportError, hideSupportError } = useSupportError();
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
  const resolveMutation = useResolveQuarantinedSerial(props.shipmentKey);

  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { note: '' },
  });

  // The keep resolution asserts Hardfin's existing record, so it is only ever
  // offered for a hold that has one.
  const existingItem = props.quarantinedSerial.existingItem;
  if (existingItem === null) {
    return null;
  }

  const copy = resolutionCopy(existingItem.sku, props.isInbound);
  // An order line for our item can be open, already filled, or absent entirely.
  const hasOpenDemand = props.quarantinedSerial.matchingOpenQuantity > 0;
  const isLineFilled =
    props.quarantinedSerial.hasMatchingLine && !hasOpenDemand;
  const existingSku = existingItem.sku;
  const reportedSku = props.quarantinedSerial.reportedItem.sku;
  // The hold names whichever order its shipment belongs to; a shipment with no
  // order at all leaves it unset.
  const orderLabel =
    props.quarantinedSerial.orderNumber === null
      ? 'This shipment'
      : `Order ${props.quarantinedSerial.orderNumber}`;

  const close = (options?: { ignorePending?: boolean }) => {
    if (resolveMutation.isPending && !options?.ignorePending) {
      return;
    }
    props.onClose();
    reset();
    setRefusalMessage(null);
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    hideSupportError();
    setRefusalMessage(null);
    resolveMutation.mutate(
      {
        quarantineKey: props.quarantinedSerial.key,
        kind: props.kind,
        note: data.note.trim(),
      },
      {
        onSuccess: () => close({ ignorePending: true }),
        onError: (error) => {
          const apiError = handleAPIError(error);
          // A cancelled shipment refuses the resolution: the shipment's own state
          // rather than a fault. It stays on a plain Alert because SupportAlert
          // tells the user support has been notified, and this path returns
          // before anything is reported.
          const curated = getUserFacingErrorMessage(apiError);
          if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
            hideSupportError();
            setRefusalMessage(curated);
            return;
          }
          setRefusalMessage(null);
          if (!apiError.sentToSentry) {
            captureException(error);
          }
          showSupportError(
            'An error occurred while resolving the held serial.'
          );
        },
      }
    );
  };

  return (
    <QuarantineResolutionDialogShell
      isOpen={props.isOpen}
      title={copy.title}
      submitLabel={copy.submitLabel}
      isPending={formState.isSubmitting || resolveMutation.isPending}
      onSubmit={handleSubmit(handleValidSubmission)}
      onClose={() => close()}
    >
      <Typography variant="body1" sx={{ mt: 2 }}>
        The 3PL reported serial{' '}
        <strong>{props.quarantinedSerial.serial}</strong> as item{' '}
        <strong>{props.quarantinedSerial.reportedItem.sku}</strong> (
        {props.quarantinedSerial.reportedItem.name}). Hardfin already tracks
        this unit as item <strong>{existingItem.sku}</strong> (
        {existingItem.name})
      </Typography>

      {hasOpenDemand && (
        <Alert severity="info" sx={{ my: 3 }}>
          <AlertTitle>{copy.consequenceTitle}</AlertTitle>
          {orderLabel} has open demand for {existingSku}. This serial will fill
          1 unit on the order. The {reportedSku} line on this order will remain
          1 short.
        </Alert>
      )}

      {isLineFilled && (
        <Alert severity="warning" sx={{ my: 3 }}>
          <AlertTitle>{copy.overfillConsequenceTitle}</AlertTitle>
          {orderLabel} has already filled demand for {existingSku}. This serial
          will be an extra unit to overfill the order. The {reportedSku} line on
          this order will remain 1 short.
        </Alert>
      )}

      {!hasOpenDemand && !isLineFilled && (
        <Alert severity="warning" sx={{ my: 3 }}>
          <AlertTitle>{copy.consequenceTitle}</AlertTitle>
          {orderLabel} has no demand line for {existingSku}. The {reportedSku}{' '}
          line on this order will remain 1 short.
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Note
      </Typography>
      <FormTextField
        control={control}
        name="note"
        fullWidth={true}
        isMultiline={true}
        minimumRows={2}
        placeholder={copy.placeholder}
      />

      {refusalMessage !== null && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {refusalMessage}
        </Alert>
      )}
    </QuarantineResolutionDialogShell>
  );
}
